import { AssignmentDTOIsClientDTO, DealDTOIsClientDTO } from 'utils/typeguards'
import { ClientGalleryOrderInformation, OrderInformationContext } from '../ClientGalleryOrderInformation'
import { FC, useEffect, useMemo } from 'react'
import { GalleryTab, useGalleryAssignment, useGalleryDeal, useGalleryNavigation, useGalleryVisualsMeta } from '../../contexts'

import { ClientGalleryContent } from '../ClientGalleryContent'
import { ClientGalleryOrderTypeFilter } from '../ClientGalleryOrderTypeFilter'
import { ClientGalleryPageActions } from '../ClientGalleryPageActions'
import { ClientGalleryRequests } from '../ClientGalleryRequests'
import DynamicQueryContent from 'components/common/DynamicQueryContent/DynamicQueryContent'
import { GalleryHeader } from 'components/common/Gallery/GalleryHeader'
import { GalleryNotFound } from '../common/GalleryNotFound'
import { MOBILE_VIEW_WIDTH_QUERY } from 'constants/styling/theme'
import { PageContent } from 'components/common/Page/PageContent'
import { PageHeader } from 'components/common/Page/PageHeader'
import { PageLayout } from 'components/common/Page/PageLayout'
import { PageTab } from 'components/common/Page/PageTab'
import { PageTabs } from 'components/common/Page/PageTabs'
import { Path } from 'constants/router/paths'
import { ProductKindsWithDocumentGallery } from 'constants/product'
import Stack from '@mui/material/Stack'
import { choosePrimaryProduct } from 'utils/product'
import { compareByShootingStart } from 'utils/time'
import { isEditingCategory } from 'utils/validators'
import { useBkbnSearchParams } from 'utils/hooks/useBkbnSearchParams'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const tabs = [GalleryTab.GALLERY, GalleryTab.REQUESTS, GalleryTab.ORDER_INFO]

/**
 * Client gallery.
 *
 * @example <ClientGalleryController />
 */
export const ClientGalleryController: FC = () => {
  const { t } = useTranslation(['gallery'])
  const isMobileView = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)

  const { assignment, assignmentData } = useGalleryAssignment()
  const {
    order,
    dealData,
    dealError,
    totalRequestCountData,
    dealAssignmentsForTab,
    orderIncludesDocumentProductWithoutMeasurementOnSiteProduct,
  } = useGalleryDeal()
  const navigate = useNavigate()
  const { listVisualsQuery } = useGalleryVisualsMeta()
  const { activeTab, setActiveTab, handleChangeTab } = useGalleryNavigation()

  const params = useBkbnSearchParams()

  // Use propertyId from search params (available immediately) with fetched data as fallback
  const propertyId = params.get('propertyId') ?? (assignmentData && AssignmentDTOIsClientDTO(assignmentData) ? assignmentData.property?.id : undefined) ?? (dealData && DealDTOIsClientDTO(dealData) ? dealData.property?.id : undefined)

  const isDealNotFound = useMemo(() => {
    return order?.isError || (order?.isSuccess && !dealData)
  }, [dealData, order?.isError, order?.isSuccess])

  const headerTitle = useMemo(() => {
    if (isDealNotFound) return t('notfound:title')

    if (!dealData || !DealDTOIsClientDTO(dealData)) return ''
    const { reference, address, productCategory } = dealData
    if (!reference && isEditingCategory(productCategory)) return t(`category:${productCategory}`)
    if (reference) return reference
    return address
  }, [dealData, isDealNotFound, t])

  const availableTabs = useMemo(() => {
    if (orderIncludesDocumentProductWithoutMeasurementOnSiteProduct) return tabs
    return tabs.filter(tab => tab !== GalleryTab.REQUESTS)
  }, [orderIncludesDocumentProductWithoutMeasurementOnSiteProduct])

  // If client come through old gallery URL, redirect to property path if propertyId exists.
  useEffect(() => {
    if (dealData && DealDTOIsClientDTO(dealData) && assignmentData) {
      const propertyId = (assignmentData && AssignmentDTOIsClientDTO(assignmentData) ? assignmentData.property?.id : undefined) ?? dealData.property?.id

      if (propertyId) {
        const primaryProduct = choosePrimaryProduct(assignmentData.products)
        const isDocument = !!primaryProduct && ProductKindsWithDocumentGallery.has(primaryProduct.kind)
        const path = isDocument ? Path.PROPERTY_DOCUMENTS_ASSIGNMENT : Path.PROPERTY_VISUALS_GALLERY_ASSIGNMENT

        navigate(path.replace(':id', propertyId.toString()).replace(':assignmentId', assignmentData.id.toString()))
      }
    }
    // navigate not dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentData, dealData])

  /** Missions by shooting start, undated last (copied, never sorting the query cache) */
  const missionsByShootingStart = useMemo(() => dealData?.missions ? [...dealData.missions].sort(compareByShootingStart) : [], [dealData?.missions])

  const showGalleryNotFound = !!assignment?.error || isDealNotFound

  if (dealData && !DealDTOIsClientDTO(dealData)) return null

  return (
    <PageLayout
      header={
        <PageHeader
          actions={!!dealData && <ClientGalleryPageActions />}
        >
          {!!headerTitle &&
            <GalleryHeader
              title={headerTitle}
              subTitle={dealData?.reference ? dealData?.address : ''}
            />
          }
        </PageHeader>
      }
      tabs={
        <PageTabs>
          {availableTabs.map((tab) => (
            <PageTab key={tab} tab={tab} tabText={t(tab)} activeTab={activeTab} totalCount={tab === GalleryTab.REQUESTS ? (totalRequestCountData ?? 0) : undefined} onClick={() => handleChangeTab(tab)} disabled={!dealData && tab !== activeTab} />
          ))}
        </PageTabs>
      }
      layoutErrorContent={showGalleryNotFound && <GalleryNotFound errorType={dealError} />}
    >
      <Stack width="100%" alignItems="center" justifyContent="center">
        <PageContent>
          {/* ASSIGNMENT TYPES FILTERS */}
          {!!dealData && activeTab === GalleryTab.GALLERY && <ClientGalleryOrderTypeFilter missions={missionsByShootingStart} onClick={(id) => navigate(`${Path.GALLERY.replace(':id', id.toString())}${propertyId ? `?propertyId=${propertyId}` : ''}`)} dealAssignmentsForTab={dealAssignmentsForTab} />}
        </PageContent>

        <DynamicQueryContent
            query={listVisualsQuery}
            showStates={['error']}
          >
            <PageContent>
              {/** VISUALS CONTENT */}
              <Stack
                height="100%"
                flex="1 1 100%"
                overflow="auto"
                alignItems="center"
                justifyContent="center"
                paddingBottom={isMobileView ? '13rem' : '8rem'}
              >
                {/** GALLERY CONTENT */}
                {!!dealData && activeTab === GalleryTab.GALLERY && <ClientGalleryContent setActiveTab={setActiveTab} />}

                {/** ORDER INFORMATION */}
                {!!dealData && activeTab === GalleryTab.ORDER_INFO && <ClientGalleryOrderInformation context={OrderInformationContext.GALLERY} />}

                {/** THREAD REQUESTS */}
                {!!dealData && activeTab === GalleryTab.REQUESTS && <ClientGalleryRequests />}
              </Stack>
            </PageContent>
          </DynamicQueryContent>

      </Stack>
    </PageLayout>
  )
}
